import React, { useEffect, useState } from 'react';
import { integrationsApi } from '../../api/integrations';
import { Notice, panel } from './ui';

const RANGES = [7, 30, 90];
const NAME = { ollama: 'Ollama', claude: 'Claude', openrouter: 'OpenRouter' };
const FEATURE = { chat: 'AI chat', assistant: 'Assistant', triage: 'Triage', workflow: 'Playbooks' };

const n = (v) => (v ?? 0).toLocaleString();
const compact = (v) => (v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(1)}k` : String(v));
const usd = (v) => (v > 0 && v < 0.01 ? '<$0.01' : `$${v.toFixed(2)}`);

// Free for local models, "Price unknown" for models without a known price, otherwise the estimate.
const costOf = (row) => {
  if (row.provider === 'ollama') return 'Free (local)';
  if (row.unpriced_tokens > 0 && row.cost === 0) return 'Price unknown';
  return usd(row.cost);
};

// One slot per calendar day (UTC, as the server groups them) from `days` ago to today, zero where nothing was used,
// so a single busy day shows as one bar on a timeline rather than filling the whole chart.
function daySlots(days, daily) {
  const byDay = new Map(daily.map((d) => [d.day, d.input_tokens + d.output_tokens]));
  const today = Date.now();
  return Array.from({ length: days + 1 }, (_, i) => {
    const day = new Date(today - (days - i) * 86_400_000).toISOString().slice(0, 10);
    return { day, total: byDay.get(day) || 0 };
  });
}

const Stat = ({ label, value, hint }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xl font-semibold">{value}</span>
    <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>{label}</span>
    {hint && <span className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>{hint}</span>}
  </div>
);

// Tokens spent per model and an estimated cost, for administrators.
export default function AiUsageCard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setError(null);
    integrationsApi.aiUsage(days)
      .then((d) => alive && setData(d))
      .catch((err) => alive && setError(err?.response?.status === 403 ? 'Only administrators can view AI usage.' : 'Could not load AI usage.'));
    return () => { alive = false; };
  }, [days]);

  const t = data?.totals;
  const slots = daySlots(data?.days ?? days, data?.daily || []);
  const peak = Math.max(1, ...slots.map((s) => s.total));
  const unpriced = t?.unpriced_tokens || 0;

  return (
    <section className="flex flex-col gap-4 px-[18px] py-4 rounded-[14px] border" style={panel}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="m-0 text-[15px] font-semibold">AI usage</h3>
          <p className="m-0 text-[12.5px] leading-snug" style={{ color: 'var(--ink-faint)' }}>
            Tokens used by each model across the assistant, chat, triage and playbooks, with an estimated cost.
          </p>
        </div>
        <div role="group" aria-label="Time range" className="flex gap-1">
          {RANGES.map((r) => (
            <button key={r} type="button" aria-pressed={days === r} onClick={() => setDays(r)}
              className="h-7 px-2.5 rounded-md border text-xs"
              style={{ borderColor: days === r ? 'var(--accent)' : 'var(--surf-line-strong)', background: days === r ? 'var(--surf-raised)' : 'transparent' }}>
              {r} days
            </button>
          ))}
        </div>
      </div>

      {error && <Notice>{error}</Notice>}
      {!data && !error && <span className="text-[13px]" style={{ color: 'var(--ink-muted)' }}>Loading…</span>}

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat label={`Estimated cost, last ${data.days} days`} value={usd(t.cost)} hint={unpriced ? `+ ${compact(unpriced)} tokens with no known price` : undefined} />
            <Stat label="Estimated cost, this month" value={usd(data.month_to_date.cost)} />
            <Stat label="Tokens in / out" value={`${compact(t.input_tokens)} / ${compact(t.output_tokens)}`} hint={t.cache_read_tokens ? `${compact(t.cache_read_tokens)} read from cache` : undefined} />
            <Stat label="Requests" value={n(t.requests)} />
          </div>

          {data.by_model.length === 0 ? (
            <p className="m-0 text-[13px]" style={{ color: 'var(--ink-muted)' }}>No AI calls in this period. Usage is counted from the moment this feature was added.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="text-left text-xs" style={{ color: 'var(--ink-faint)' }}>
                      <th className="py-1.5 pr-3 font-medium">Model</th>
                      <th className="py-1.5 pr-3 font-medium text-right">Requests</th>
                      <th className="py-1.5 pr-3 font-medium text-right">Input</th>
                      <th className="py-1.5 pr-3 font-medium text-right">Output</th>
                      <th className="py-1.5 font-medium text-right">Est. cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_model.map((m) => (
                      <tr key={`${m.provider}/${m.model}`} className="border-t" style={{ borderColor: 'var(--surf-line)' }}>
                        <td className="py-2 pr-3"><span className="font-medium">{m.model}</span> <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>{NAME[m.provider] || m.provider}</span></td>
                        <td className="py-2 pr-3 text-right tabular-nums">{n(m.requests)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{n(m.input_tokens)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{n(m.output_tokens)}</td>
                        <td className="py-2 text-right tabular-nums">{costOf(m)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs" style={{ color: 'var(--ink-muted)' }}>
                {data.by_feature.map((f) => <span key={f.feature}>{FEATURE[f.feature] || f.feature}: {compact(f.input_tokens + f.output_tokens)} tokens</span>)}
              </div>

              <div className="flex flex-col gap-1">
                <div role="img" aria-label="Tokens per day" className="flex items-end gap-[3px] h-16">
                  {slots.map(({ day, total }) => (
                    <div key={day} data-day={day} title={`${day}: ${n(total)} tokens`} className="flex-1 min-w-[2px] rounded-t-sm"
                      style={total ? { height: `${Math.max(6, (total / peak) * 100)}%`, background: 'var(--accent)' } : { height: 2, background: 'var(--surf-line-strong)' }} />
                  ))}
                </div>
                <div className="flex justify-between text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                  <span>{slots[0].day}</span>
                  <span>Busiest day: {compact(peak)} tokens</span>
                  <span>{slots[slots.length - 1].day}</span>
                </div>
              </div>
            </>
          )}

          <span className="text-xs leading-snug" style={{ color: 'var(--ink-faint)' }}>
            Costs are estimates from list prices and may differ from your provider's invoice. Local Ollama models are free. Models without a known price show tokens only.
          </span>
        </>
      )}
    </section>
  );
}
